import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { QueryBaseSchema } from '@pkg/api/query/QueryFilterSchema'
import { defineEntityConfig } from '@pkg/db/schema/EntityConfig'
import { formatAddress } from '@pkg/shared/utils'
import { ButtonLink } from '@pkg/ui/button'
import { LinkIndicatorIcon } from '@pkg/ui/common'
import { FormattedNameAndAddress } from '@pkg/ui/formattedContent/NameAndAddress'
import { FaHouseChimneyMedical } from 'react-icons/fa6'
import { z } from 'zod'
import { MedicalSupplyHomeSchema } from './schema/MedicalSupplyHome'

const label = t`Sanitätshaus`

export const MedicalSupplyHomeConfig = defineEntityConfig(() => ({
  schema: MedicalSupplyHomeSchema,

  label,
  labelPlural: t`Sanitätshäuser`,

  icon: FaHouseChimneyMedical,

  route: {
    list: '/sanitaetshaeuser',
    edit: '/sanitaetshaeuser/$id',
    new: '/sanitaetshaeuser/new',
  },

  relationSelectionColumns: [
    {
      key: 'medicalSupplyHome.name',
      title: label,
      sorter: true,
      render: (_, row) => <FormattedNameAndAddress name={row.name} address={row.address ?? undefined} />,
    },
    {
      title: t`Versorgungsbereich`,
      key: 'medicalSupplyHome.serviceArea',
      sorter: true,
      render: (_, record) => record.serviceArea,
    },
  ],

  indexColumns: [
    {
      key: 'medicalSupplyHome.name',
      title: label,
      sorter: true,
      fixed: 'start',
      render: (_, row) => (
        <FormattedNameAndAddress
          name={row.name}
          address={row.address ?? undefined}
          link={{ to: '/sanitaetshaeuser/$id', params: { id: row.id } }}
        />
      ),
    },
    {
      title: t`Versorgungsbereich`,
      key: 'medicalSupplyHome.serviceArea',
      sorter: true,
      render: (_, record) => record.serviceArea,
    },
    {
      key: 'actions',
      title: <Trans>Aktionen</Trans>,
      sorter: false,
      fixed: 'end',
      render: (_, row) => (
        <ButtonLink to="/sanitaetshaeuser/$id" params={{ id: row.id }} size="small" shape="round">
          <Trans>Anzeigen</Trans>
          <LinkIndicatorIcon />
        </ButtonLink>
      ),
    },
  ],

  search: {
    filterSchema: QueryBaseSchema.extend({
      sortBy: z
        .enum([
          'medicalSupplyHome.name',
          'medicalSupplyHome.address.streetAddress',
          'medicalSupplyHome.address.zipCode',
          'medicalSupplyHome.address.city',
          'medicalSupplyHome.serviceArea',
        ])
        .nullish(),
    }),
    sortableFields: ['name', 'serviceArea', 'createdAt'] as const,
    defaultSortBy: 'name',
    defaultSortOrder: 'asc',
  },

  format: {
    primary: val => val?.name,
    secondary: val => (val ? formatAddress([], val.address) : undefined),
  },
}))
